import { ReactNode } from 'react'
import { SpaceType } from 'src/components/SearchBar/types'
import {
  ICoordinates,
  IPhotoInput,
  LocationType,
  SizeFilter,
  SortBy
} from './graphql'

export type IBounds = {
  latTop: number
  latBot: number
  lngLeft: number
  lngRight: number
}

export type IMarker = { id: string; coordinates: ICoordinates }

export type IQueryParams = {
  search: string | undefined
  placeId: string | undefined
  description: string | undefined

  lat: number | undefined
  lng: number | undefined
  zoom: number | undefined

  sortBy: SortBy | undefined

  minPrice: number | undefined
  maxPrice: number | undefined
  canStoreVehicle: boolean | undefined
  sizes: SizeFilter[] | undefined
  spaceTypes: SpaceType[] | undefined
  locationTypes: LocationType[] | undefined

  page: number

  staticPage?: boolean

  // mode
  hood: string | undefined | null
  city: string | undefined | null
  zipcode: string | undefined | null
  state: string | undefined | null
}

export type IPrivatePhotoInput = IPhotoInput & { signedUrl?: string | null }

export enum RedirectUrls {
  Home = '/',

  TermsAndConditions = '/terminos-y-condiciones',
  Disclosures = '/aviso-de-privacidad',

  Login = '/login',
  Authenticate = '/authenticate',
  ConfirmEmail = '/confirm-email',
  AuthAction = '/auth/action',

  Anfitrion = '/anfitrion',
  Negocio = '/anfitrion/negocio',
  Tradicional = '/anfitrion/tradicional',
  Estacionamiento = '/anfitrion/estacionamiento',
  Calculate = '/anfitrion/valua-tu-propiedad',
  SolucionesLogisticas = '/soluciones-logisticas',
  AnfitrionLogistica = '/anfitrion/soluciones-logisticas',
  Desarrolladores = '/anfitrion/desarrolladores',

  Directory = '/directory',
  /**
   * One-click directory removal (plan §3.6). Deliberately NOT in the sitemap,
   * the footer or `pages/directory/index.tsx`, and `noindex,nofollow`: the page
   * is meaningless without a signed token, and indexing a "remove your listing"
   * URL invites people to guess at one.
   */
  BajaDirectorio = '/baja-directorio',
  /**
   * "Activar depósitos" — public, token-authenticated page that takes a host
   * straight into Stripe's hosted onboarding with no login
   * (`/pagos/activar/[token]`, returns to `/pagos/activar/listo`). The token
   * is the credential; `noindex,nofollow`, never linked from navigation.
   */
  PayoutActivation = '/pagos/activar',

  Help = '/acerca-de-spotme',
  Contact = '/contacto',
  Nosotros = '/nosotros',
  Medios = '/medios',
  Blog = '/blog',
  Mcp = '/mcp',
  Seguro = '/seguro',
  Reembolso = '/politica-de-reembolso',
  Disputas = '/disputas',

  Refer = '/anfitrion/invita',
  Broker = '/anfitrion/broker',
  Bodegas = '/anfitrion/bodegas',

  Search = '/search',
  BeginSearch = '/begin-search',
  Details = '/alojamientos',
  User = '/user',
  Business = '/business',
  BusinessTeam = '/business/team',

  Dashboard = '/dashboard/host',
  DashboardHome = '/dashboard',
  DashboardRent = '/dashboard/rent',
  Listings = '/dashboard/host/listings',
  Rents = '/dashboard/host/rents',
  HostProfile = '/dashboard/host/profile',
  HostEditProfile = '/dashboard/host/editProfile',
  HostMessages = '/dashboard/host/messages',
  Referral = '/dashboard/host/referral',
  Team = '/dashboard/host/team',
  HostClosedDeals = '/dashboard/host/closed-deals',
  HostInteresados = '/dashboard/host/interesados',
  HostInventario = '/dashboard/host/inventario',
  HostPayments = '/dashboard/host/pagos',

  CheckoutDetails = '/checkoutDetails',

  CompleteProfile = '/complete-profile',

  Reservations = '/dashboard/rent/reservations',
  RentMessages = '/dashboard/rent/messages',
  RentProfile = '/dashboard/rent/profile',
  RentEditProfile = '/dashboard/rent/editProfile',

  BankInfo = '/dashboard/bankInfo',
  EditBankInfo = '/dashboard/editBankInfoUser',
  Admin = '/dashboard/admin',
  /**
   * SAT fiscal-compliance portal. Gated by user.isSatAuditor or user.isAdmin.
   * Intentionally NOT in nav, footer, or sitemap — direct URL only. The
   * credential package handed to SAT (see legal/compliance/sat-portal/SAT-PORTAL-INSTRUCTIVO.md)
   * contains this URL plus username + password.
   */
  SatPortal = '/sat-portal',

  UserConfirmation = '/userConfirmation',
  SubmitLocation = '/post',
  Checkout = '/checkout',
  PhonePrompt = '/phoneConfirmation',
  Suspended = '/suspended',

  SizeGuide = '/guia-de-tamanos',

  /**
   * Pre-auth reservation start page, child route of any Monthly listing:
   * `/[pillar]/[city]/[hood]/[configId]/reservar`. Routed to by the
   * `Reservar` CTA on the listing page when `currentUser` is null. Captures
   * phone-as-identity via Firebase Phone Auth + creates a HubSpot lead the
   * moment the phone is submitted, BEFORE the OTP is verified.
   */
  PreAuthReservar = 'reservar'
}

export enum IFilterCanStoreVehicle {
  Both = 'BOTH',
  Vehicle = 'VEHICLE',
  SelfStorage = 'SELF_STORAGE'
}

export enum CheckoutView {
  Reservation = 'reservation',
  Rent = 'rent'
}

export type IArticle = {
  category: RedirectUrls
  article: { header: string; content: string | string[] | ReactNode }[]
}

export type IArticleSearchable = {
  title: string
  description: string
  href: RedirectUrls
  body: string
}

/**
 * Slim, build-time search index entry for the help-center hero search.
 * `searchText` is the lowercased concatenation of title + description + FAQ
 * question titles + primary/secondary keywords from structuredData. Substring
 * matched in HelpBanner.tsx — no network round-trip per keystroke.
 */
export type IHelpSearchEntry = {
  title: string
  description: string
  href: string
  searchText: string
}
